import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Send, X, Save, Eye, RefreshCw, Gift, Clock, Star, ExternalLink, History, Repeat, BadgePercent } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = { nome: 'Maria', servico: 'Deep Cleaning', data: '2026-09-20', hora: '10:00', morada: 'Dublin 4, D04 X1Y2', estimativa: '€180', desconto: '10%', link_avaliacao: 'https://g.page/r/sancur/review' };
const render = (tpl, values) => Object.entries(values).reduce((t, [k, v]) => t.split(`{${k}}`).join(v), tpl || '');
const waLink = (phone, text) => `https://wa.me/${String(phone || '').replace(/[^\d]/g, '').replace(/^0/, '353')}?text=${encodeURIComponent(text)}`;

export const ReminderCard = ({ r, language, onSent, onDismiss }) => {
  const [lang, setLang] = useState(r.default_lang || 'pt');
  const text = lang === 'pt' ? r.message_pt : r.message_en;
  return (
    <div className="bg-white border rounded-xl p-4 flex flex-col gap-3" data-testid={`reminder-card-${r.appointment_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900" data-testid="reminder-client-name">{r.name}</p>
          <p className="text-sm text-gray-600">{r.service_type} · {r.preferred_date}</p>
          <p className="text-xs text-gray-400">{r.phone}</p>
        </div>
        <div className="flex gap-1 text-xs">
          {['pt', 'en'].map(l => (
            <button key={l} onClick={() => setLang(l)} data-testid={`reminder-lang-${l}`}
              className={`px-2 py-1 rounded-full border ${lang === l ? 'bg-[#1a5c3a] text-white border-[#1a5c3a]' : 'text-gray-600'}`}>{l.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-body" data-testid="reminder-preview">{text}</pre>
      <div className="flex gap-2">
        <a href={waLink(r.phone, text)} target="_blank" rel="noopener noreferrer" onClick={() => onSent(r.appointment_id)}
          data-testid="reminder-send-button"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-2 px-4 rounded-full transition-colors">
          <Send size={16} /> {language === 'pt' ? 'Enviar WhatsApp' : 'Send WhatsApp'}
        </a>
        <button onClick={() => onDismiss(r.appointment_id)} data-testid="reminder-dismiss-button"
          className="px-3 py-2 rounded-full border text-gray-500 hover:bg-gray-50" title={language === 'pt' ? 'Ignorar' : 'Dismiss'}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const TemplateField = ({ label, value, onChange, testId, sample }) => {
  const [preview, setPreview] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <button type="button" onClick={() => setPreview(p => !p)} data-testid={`${testId}-preview-toggle`}
          className="text-xs inline-flex items-center gap-1 text-[#1a5c3a] hover:underline"><Eye size={14} /> Preview</button>
      </div>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={7} data-testid={testId}
        className="w-full border rounded-lg p-3 text-sm font-body focus:ring-2 focus:ring-[#1a5c3a] focus:outline-none" />
      {preview && <pre className="text-xs text-gray-700 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg p-3 whitespace-pre-wrap font-body" data-testid={`${testId}-preview`}>{render(value, sample)}</pre>}
    </div>
  );
};

const AdminWhatsApp = ({ language, token, onCountChange }) => {
  const [reminders, setReminders] = useState([]);
  const [history, setHistory] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const pt = language === 'pt';

  const load = useCallback(async () => {
    try {
      const [r, t, h] = await Promise.all([
        axios.get(`${API}/admin/reminders/pending`, { headers }),
        axios.get(`${API}/whatsapp-templates`),
        axios.get(`${API}/admin/reminders/history`, { headers })
      ]);
      setReminders(r.data.reminders);
      onCountChange?.(r.data.count);
      setTpl(t.data);
      setHistory(h.data);
    } catch (e) { toast.error(pt ? 'Erro ao carregar' : 'Failed to load'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const removeLocal = (id) => setReminders(prev => { const next = prev.filter(r => r.appointment_id !== id); onCountChange?.(next.length); return next; });
  const markSent = async (id) => {
    await axios.post(`${API}/admin/reminders/${id}/sent`, {}, { headers });
    removeLocal(id);
    toast.success(pt ? 'Lembrete marcado como enviado' : 'Reminder marked as sent');
    axios.get(`${API}/admin/reminders/history`, { headers }).then(h => setHistory(h.data)).catch(() => {});
  };
  const dismiss = async (id) => { await axios.post(`${API}/admin/reminders/${id}/dismiss`, {}, { headers }); removeLocal(id); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/whatsapp-templates`, {
        quote_pt: tpl.quote_pt, quote_en: tpl.quote_en, reminder_pt: tpl.reminder_pt, reminder_en: tpl.reminder_en,
        discount: tpl.discount, review_link: tpl.review_link, reminder_delay_hours: Number(tpl.reminder_delay_hours) || 24
      }, { headers });
      setTpl(data);
      toast.success(pt ? 'Mensagens guardadas' : 'Templates saved');
    } catch (e) { toast.error(pt ? 'Erro ao guardar' : 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (!tpl) return <div className="p-8 text-center text-gray-500">{pt ? 'A carregar...' : 'Loading...'}</div>;
  const sample = { ...SAMPLE, desconto: tpl.discount || '10%', link_avaliacao: tpl.review_link || (pt ? '(link em falta)' : '(link missing)') };

  return (
    <div className="space-y-8" data-testid="whatsapp-section">
      <Toaster position="top-right" richColors />
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2">
              <Gift className="text-[#25D366]" size={22} /> {pt ? 'Lembretes prontos a enviar' : 'Reminders ready to send'}
              <span className="text-sm bg-[#25D366] text-white rounded-full px-2 py-0.5" data-testid="reminders-count">{reminders.length}</span>
            </h2>
            <p className="text-sm text-gray-600">{pt ? `Clientes com serviço concluído há mais de ${tpl.reminder_delay_hours}h. Toque em Enviar para abrir o WhatsApp com a mensagem pronta.` : `Clients whose service completed over ${tpl.reminder_delay_hours}h ago. Tap Send to open WhatsApp with the message ready.`}</p>
          </div>
          <button onClick={load} className="p-2 rounded-full border hover:bg-gray-50" data-testid="reminders-refresh"><RefreshCw size={16} /></button>
        </div>
        {reminders.length === 0 ? (
          <div className="bg-gray-50 border border-dashed rounded-xl p-8 text-center text-gray-500" data-testid="reminders-empty">{pt ? 'Sem lembretes pendentes.' : 'No pending reminders.'}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {reminders.map(r => <ReminderCard key={r.appointment_id} r={r} language={language} onSent={markSent} onDismiss={dismiss} />)}
          </div>
        )}
      </section>

      <section data-testid="reminder-history-section">
        <h2 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <History className="text-[#1a5c3a]" size={22} /> {pt ? 'Histórico de lembretes' : 'Reminder history'}
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: pt ? 'Enviados' : 'Sent', value: history?.total_sent ?? 0, icon: Send, color: 'text-[#25D366]', id: 'sent' },
            { label: pt ? 'Voltaram a marcar' : 'Rebooked', value: history?.total_rebooked ?? 0, icon: Repeat, color: 'text-blue-600', id: 'rebooked' },
            { label: pt ? 'Com desconto' : 'With discount', value: history?.total_rebooked_with_discount ?? 0, icon: BadgePercent, color: 'text-amber-600', id: 'discount' },
          ].map(c => (
            <div key={c.id} className="bg-white border rounded-xl p-4" data-testid={`history-stat-${c.id}`}>
              <div className={`flex items-center gap-2 text-sm text-gray-500 mb-1`}><c.icon size={16} className={c.color} /> {c.label}</div>
              <div className="text-2xl font-bold text-gray-900">{c.value}</div>
            </div>
          ))}
        </div>
        {!history || history.history.length === 0 ? (
          <div className="bg-gray-50 border border-dashed rounded-xl p-6 text-center text-gray-500" data-testid="history-empty">{pt ? 'Ainda não foram enviados lembretes.' : 'No reminders sent yet.'}</div>
        ) : (
          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-table">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{pt ? 'Cliente' : 'Client'}</th>
                  <th className="px-4 py-2 font-medium">{pt ? 'Serviço' : 'Service'}</th>
                  <th className="px-4 py-2 font-medium">{pt ? 'Enviado em' : 'Sent on'}</th>
                  <th className="px-4 py-2 font-medium">{pt ? 'Voltou a marcar' : 'Rebooked'}</th>
                </tr>
              </thead>
              <tbody>
                {history.history.map(h => (
                  <tr key={h.appointment_id} className="border-t" data-testid={`history-row-${h.appointment_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{h.name}</div>
                      <div className="text-xs text-gray-400">{h.phone}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{h.service_type}<div className="text-xs text-gray-400">{h.service_date}</div></td>
                    <td className="px-4 py-2 text-gray-700">{h.sent_at ? new Date(h.sent_at).toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                    <td className="px-4 py-2">
                      {h.rebooked ? (
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full"><Repeat size={12} /> {h.rebooked_date}</span>
                          {h.rebooked_with_discount && <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">−€{Number(h.rebooked_discount_amount).toFixed(2)}</span>}
                        </div>
                      ) : <span className="text-xs text-gray-400">{pt ? 'Ainda não' : 'Not yet'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border p-6 space-y-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-gray-900 flex items-center gap-2"><MessageCircle className="text-[#25D366]" size={22} /> {pt ? 'Mensagens WhatsApp' : 'WhatsApp messages'}</h2>
          <p className="text-sm text-gray-600 mt-1">{pt ? 'Variáveis disponíveis:' : 'Available variables:'} <code className="text-xs bg-gray-100 px-1 rounded">{'{nome} {servico} {data} {hora} {morada} {estimativa} {desconto} {link_avaliacao}'}</code></p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><Gift size={14} /> {pt ? 'Desconto próxima marcação' : 'Next booking discount'}</label>
            <input value={tpl.discount || ''} onChange={e => setTpl({ ...tpl, discount: e.target.value })} data-testid="wa-discount-input"
              className="mt-1 w-full border rounded-lg p-2 text-sm" placeholder="10%" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><Clock size={14} /> {pt ? 'Horas após conclusão' : 'Hours after completion'}</label>
            <input type="number" min={1} value={tpl.reminder_delay_hours} onChange={e => setTpl({ ...tpl, reminder_delay_hours: e.target.value })} data-testid="wa-delay-input"
              className="mt-1 w-full border rounded-lg p-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><Star size={14} className="text-amber-500" /> {pt ? 'Link de avaliação Google' : 'Google review link'}</label>
          <div className="mt-1 flex gap-2">
            <input value={tpl.review_link || ''} onChange={e => setTpl({ ...tpl, review_link: e.target.value })} data-testid="wa-review-link-input"
              className="flex-1 border rounded-lg p-2 text-sm" placeholder="https://g.page/r/.../review" />
            {tpl.review_link && (
              <a href={tpl.review_link} target="_blank" rel="noopener noreferrer" data-testid="wa-review-link-open"
                className="inline-flex items-center gap-1 px-3 rounded-lg border text-sm text-[#1a5c3a] hover:bg-gray-50"><ExternalLink size={14} /> {pt ? 'Abrir' : 'Open'}</a>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">{pt ? 'Usado na variável {link_avaliacao} do lembrete pós-serviço.' : 'Used by the {link_avaliacao} variable in the post-service reminder.'}</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <TemplateField label={pt ? 'Orçamento — cliente (PT)' : 'Quote — client (PT)'} value={tpl.quote_pt} onChange={v => setTpl({ ...tpl, quote_pt: v })} testId="wa-quote-pt" sample={sample} />
          <TemplateField label={pt ? 'Orçamento — cliente (EN)' : 'Quote — client (EN)'} value={tpl.quote_en} onChange={v => setTpl({ ...tpl, quote_en: v })} testId="wa-quote-en" sample={sample} />
          <TemplateField label={pt ? 'Lembrete pós-serviço (PT)' : 'Post-service reminder (PT)'} value={tpl.reminder_pt} onChange={v => setTpl({ ...tpl, reminder_pt: v })} testId="wa-reminder-pt" sample={sample} />
          <TemplateField label={pt ? 'Lembrete pós-serviço (EN)' : 'Post-service reminder (EN)'} value={tpl.reminder_en} onChange={v => setTpl({ ...tpl, reminder_en: v })} testId="wa-reminder-en" sample={sample} />
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="wa-save-button"
            className="inline-flex items-center gap-2 bg-[#1a5c3a] hover:bg-[#164d31] disabled:opacity-60 text-white font-semibold py-2 px-6 rounded-full transition-colors">
            <Save size={16} /> {saving ? (pt ? 'A guardar...' : 'Saving...') : (pt ? 'Guardar mensagens' : 'Save messages')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminWhatsApp;
